import { PaperAirplaneIcon, XCircleIcon } from "@heroicons/react/16/solid";
import { cn } from "@moe/oss/utils/utils";
import { AutosizeTextarea, AutosizeTextAreaRef } from "@moe/priv/components/autosize-text-area";
import { useKeyDown } from "@moe/priv/hooks/use-key-down";
import { useScaleToParent } from "@moe/priv/hooks/useScaleToParent";
import { useDataContext } from "@web/route-services/chat/data-context";
import { GroupChatTurnControl } from "@web/route-services/chat/GroupChatTurnControl";
import { Typing } from "@web/route-services/chat/message/Typing";
import { useServiceContext } from "@web/route-services/chat/service-context";
import { useAppContext } from "@web/route-services/root/AppContext";
import { ArrowDownUpIcon } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useChatAnalytics } from "@web/route-services/chat/use-chat-analytics";

interface Props {
  className?: string;
  [x: string]: any;
}

const enum ProcessingState {
  IDLE = "IDLE",
  PROCESSING = "PROCESSING",
  ABORTING = "ABORTING"
}

export function Input({ className, ...rest }: Props) {
  const [input, setInput] = useState<string>("");
  const { chars, chatID, persona } = useDataContext();
  const [turnControlOpen, setTurnControlOpen] = useState(chars.size > 1);
  const { isDesktopScreen } = useAppContext();
  const { isTouchDevice } = useAppContext();
  const rootRef = useRef<HTMLDivElement>(null);
  const analytics = useChatAnalytics();

  const textAreaRef = useRef<AutosizeTextAreaRef | null>(null);
  const placeholder = isTouchDevice ? "Press button to send." : "Press Enter to send, Shift+Enter for newline";
  const isInputPresent = input.length > 0;
  const [showActionLabel, setShowActionLabel] = useState(true);

  const { isGenerating, service } = useServiceContext();
  const [processingState, setProcessingState] = useState<ProcessingState>(ProcessingState.IDLE);
  const processingTimeoutRef = useRef<NodeJS.Timeout>();
  const isProcessing = processingState !== ProcessingState.IDLE;

  // State synchronization
  useEffect(() => {
    if (processingState === ProcessingState.ABORTING) {
      return;
    }
    if (isGenerating) {
      setProcessingState(ProcessingState.PROCESSING);
    } else {
      processingTimeoutRef.current = setTimeout(() => {
        setProcessingState(ProcessingState.IDLE);
      }, 100);
    }

    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, [isGenerating, processingState]);

  /**
   * Optimistically sends the current input message and clears the input field.
   * If there is an error sending the message, restores the original input.
   */
  const optimisticSend = async () => {
    const currentInput = input;
    try {
      setProcessingState(ProcessingState.PROCESSING);
      setInput("");
      // Track message sent event
      analytics.trackMessageSent(
        chatID,
        currentInput.length,
        chars.size,
        persona?.id
      );
      await service.send(input);
    } catch (e) {
      toast.error(`Error sending message :<`);
      console.error(e);
      setInput(currentInput);
    }
  };

  /**
   * Abort on escape key
   */
  useKeyDown({
    Escape: () => {
      const inputFocused = document.activeElement === textAreaRef.current;
      if (inputFocused) return;
      service.abort();
    }
  });

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // On non new-line enter
    if (e.key === "Enter" && !e.shiftKey && !isTouchDevice) {
      e.preventDefault();
      // Do nothing if we're already generating a response
      if (isProcessing) {
        toast("Character is still typing, please wait a moment");
        return;
      }

      // If the input is empty, ask the character to continue
      if (input.length === 0) {
        // Track continue event
        analytics.trackChatContinue(chatID, chars.size, persona?.id);
        service.continue();
        return;
      }

      // Send the message if it's not empty
      optimisticSend();
    }
  };

  const actionButtonHandler = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    /**
     * Prevent the text area from being focused when the button is clicked
     */
    e.stopPropagation();

    // Prevent rapid toggling by checking current state
    if (processingState === ProcessingState.ABORTING) {
      return;
    }

    // Abort button if we're generating
    if (isProcessing) {
      setProcessingState(ProcessingState.ABORTING);
      await service.abort();
      // setIsAnimating(false); // Placeholder for animation state
      await new Promise((resolve) => setTimeout(resolve, 150)); // Delay to prevent rapid toggling
      setProcessingState(ProcessingState.IDLE);
      return;
    }
    // Continue button if the input is empty
    if (input.length === 0) {
      // Track continue event
      analytics.trackChatContinue(chatID, chars.size, persona?.id);
      service.continue();
      return;
    }
    // Send the message if it's not empty
    optimisticSend();
    // On mobile, we want to unfocus the text area so the virtual keyboard goes away
    if (isTouchDevice) {
      textAreaRef.current?.textArea.blur();
    }
  };

  useScaleToParent(rootRef, {
    enabled: isDesktopScreen,
    offsetPx: 10
  });

  /**
   * Hide the action label if once the user input is close to the end of the TextArea
   * This saves horizontal screen space when users are typing long messages
   */
  const updateShowActionLabel = (text: string) => {
    const computedStyle = window.getComputedStyle(textAreaRef.current?.textArea!);
    const font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
    const textWidth = getTextWidth(text, font);
    const textAreaClientWidth = textAreaRef.current?.textArea.clientWidth;
    if (!textAreaClientWidth) return;
    const textAreaWidth =
      textAreaClientWidth - (parseInt(computedStyle.paddingLeft) + parseInt(computedStyle.paddingRight));
    const threshold = showActionLabel ? 0.95 : 0.75;
    setShowActionLabel(textWidth < textAreaWidth * threshold);
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        `border-line fixed bottom-0 flex w-full flex-col rounded-t-[32px]
        border-x border-t bg-[hsl(234,20.6%,13%)]/[0.96]
        pt-1 shadow-[0_-6px_15px_-3px_rgba(0,0,0,0.15)]
        backdrop-blur-xl md:pb-3`,
        className
      )}
      {...rest}
    >
      <div className="bg-transparent">
        <Typing isTyping={isProcessing} className="mb-0 ml-6" name={"Character"} />
        <GroupChatTurnControl open={turnControlOpen} className="-mb-[7px] md:-mb-[10px]" />
      </div>

      <div
        className={`z-10 bg-transparent px-2 py-1`}
        onClick={() => {
          textAreaRef.current?.textArea.focus();
        }}
      >
        {/* Input Bar Wrapper */}
        <div className={`bg-inherit" rounded-4xl flex h-fit items-start overflow-auto px-4 `}>
          {/* Turn Control Toggler */}
          {chars.size > 1 && (
            <motion.button
              className="px-0.5 pb-0 pt-[12px]"
              onClick={(e) => {
                e.stopPropagation();
                setTurnControlOpen((prev) => !prev);
              }}
            >
              <ArrowDownUpIcon
                strokeWidth={2.2}
                className={`text-action-secondary hover:text-tx-primary size-6 transition duration-300 ease-out  ${
                  turnControlOpen ? "rotate-180" : ""
                }`}
              />
            </motion.button>
          )}
          <AutosizeTextarea
            minHeight={36}
            ref={textAreaRef}
            autoFocus={!isTouchDevice}
            className="scroll-chat-chatbar text-tx-primary placeholer:overflow-none h-[50px] w-full resize-none overflow-y-auto border-none bg-inherit py-[12px] font-medium leading-6
            placeholder:select-none focus:outline-none disabled:cursor-not-allowed md:pr-8"
            placeholder={placeholder}
            maxHeight={200}
            onChange={(e) => {
              setInput(e.target.value);
              updateShowActionLabel(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            value={input}
          />

          {/* Action Button */}
          <button
            onClick={actionButtonHandler}
            className="group mt-[3px] flex h-fit items-center justify-center"
            disabled={processingState === ProcessingState.ABORTING}
          >
            {/* Show abort button when generating */}
            {isProcessing && (
              <div
                className="relative flex min-w-[90px] items-center justify-end rounded-3xl px-3 
                py-2"
              >
                {showActionLabel && (
                  <span
                    className="text-action-secondary group-hover:text-button-highlight-hover 
                    group-active:text-action-tertiary 
                    transition-color 
                    text-sm
                    font-semibold duration-300 ease-out"
                  >
                    Abort
                  </span>
                )}
                <XCircleIcon
                  className="fill-action-secondary group-hover:fill-button-highlight-hover
                  group-active:fill-action-tertiary 
                  transition-color 
                  ml-2
                  size-7 duration-300 ease-out"
                />
              </div>
            )}

            {/* Show continue or send button when not generating */}
            {!isProcessing && (
              <div
                className={`relative flex items-center justify-end ${isDesktopScreen && "min-w-[80px]"} rounded-3xl px-3
                py-2`}
              >
                {showActionLabel && (
                  <span
                    className="text-action-secondary group-hover:text-button-highlight-hover group-active:text-action-tertiary
                    transition-color 
                    mr-2 
                    whitespace-nowrap
                    text-sm font-semibold duration-300
                    ease-out"
                  >
                    {isInputPresent ? "Send" : "Continue"}
                  </span>
                )}
                <PaperAirplaneIcon
                  className="fill-action-secondary
                    group-hover:fill-button-highlight-hover 
                    group-active:fill-action-tertiary 
                    transition-color
                    size-7 duration-300 ease-out"
                />
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Calculates the width of a given text string using the specified font.
 * @param text - The text string to measure.
 * @param font - The font to use for the measurement.
 * @returns The width of the text in pixels.
 */
const getTextWidth = (text: string, font: string) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (context) {
    context.font = font;
    return context.measureText(text).width;
  }
  return 0;
};
